import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { ReaddirResult } from '@capacitor/filesystem';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { CommunityDevice } from '@capacitor-community/device';
import type { CommunityDeviceInfo } from '@capacitor-community/device';
import type { Observable } from 'rxjs';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import type { StorageInformation } from './storage-information';

@Injectable({
  providedIn: 'root',
})
export class StorageInformationService {
  public getFreeDeviceStorageSize$(): Observable<number | undefined> {
    return of(null).pipe(
      switchMap(() => from(CommunityDevice.getInfo())),
      map((deviceInfo: CommunityDeviceInfo) => deviceInfo.realDiskFree),
      catchError(() => of(undefined))
    );
  }

  public getStorageInformation$(): Observable<StorageInformation> {
    return from(CommunityDevice.getInfo()).pipe(
      switchMap((deviceInfo: CommunityDeviceInfo) => {
        return from(this.getAppStorageUsage()).pipe(
          switchMap(allocatedByOurApp => {
            const adjustedStorageSize = this.calculateAdjustedStorageSize(deviceInfo.realDiskTotal);
            const freeStorageSize = deviceInfo.realDiskFree;
            let storageSizeCorrectionValue = 0;

            if (Capacitor.getPlatform() === 'android' && adjustedStorageSize) {
              const detectedChipSize = this.guessStorageCapacity(adjustedStorageSize / 1000 ** 3);
              storageSizeCorrectionValue = detectedChipSize * 1000 ** 3 - adjustedStorageSize;
            }

            return this.composeStorageInformation(adjustedStorageSize, freeStorageSize, allocatedByOurApp, storageSizeCorrectionValue);
          })
        );
      })
    );
  }

  private readonly storageCapacities = [16, 32, 64, 128, 256, 512, 1024, 2048, 4096, 8192];

  private readonly guessStorageCapacity = (actualGB: number): number => {
    for (const size of this.storageCapacities) {
      if (actualGB <= size) {
        return size;
      }
    }

    return this.storageCapacities.at(-1) as number;
  };

  private readonly composeStorageInformation = (
    totalStorageSize: number | undefined,
    freeStorageSize: number | undefined,
    allocatedByOurApp: number | undefined,
    storageSizeCorrectionValue: number
  ) => {
    const correctedTotalStorageSize = totalStorageSize && storageSizeCorrectionValue !== undefined ? totalStorageSize + storageSizeCorrectionValue : undefined;

    const allocatedByOtherApps =
      totalStorageSize && freeStorageSize && allocatedByOurApp
        ? totalStorageSize - freeStorageSize - allocatedByOurApp + storageSizeCorrectionValue
        : undefined;

    const totalAllocatedPercent =
      correctedTotalStorageSize && freeStorageSize
        ? Math.ceil(((correctedTotalStorageSize - freeStorageSize) / correctedTotalStorageSize) * 100) / 100
        : undefined;

    return of({
      totalStorageSize: correctedTotalStorageSize,
      allocatedByOtherApps,
      allocatedByOurApp,
      totalAllocatedPercent,
      freeStorageSize,
    } as StorageInformation);
  };

  private readonly calculateAdjustedStorageSize = (total: number | undefined) => {
    return total ? Math.ceil(total / 1000 ** 3) * 1000 ** 3 : undefined;
  };

  private readonly getDirectorySize = async (path: string = '', directory: Directory = Directory.Data): Promise<number> => {
    let total = 0;

    try {
      const directoryContents: ReaddirResult = await Filesystem.readdir({
        path,
        directory,
      });

      for (const file of directoryContents.files) {
        const filePath = path ? `${path}/${file.name}` : file.name;
        const stat = await Filesystem.stat({ path: filePath, directory });

        total += stat.type === 'directory' ? await this.getDirectorySize(filePath, directory) : (stat.size ?? 0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Unable to read:', path, error);
    }

    return total;
  };

  private readonly getAppStorageUsage: () => Promise<number | undefined> = async () => {
    switch (Capacitor.getPlatform()) {
      case 'android': {
        let userData = 0;
        userData += await this.getDirectorySize('', Directory.Data);
        userData += await this.getDirectorySize('../app_webview', Directory.Data);
        userData += await this.getDirectorySize('../shared_prefs', Directory.Data);

        const cache = await this.getDirectorySize('', Directory.Cache);

        return userData + cache;
      }

      case 'ios': {
        const userData = await this.getDirectorySize('WebKit/WebsiteData', Directory.Library);
        const cache = await this.getDirectorySize('', Directory.Cache);

        return userData + cache;
      }
    }

    // eslint-disable-next-line unicorn/no-useless-undefined
    return undefined;
  };
}
